import {
  addWeeks,
  isToday,
  isTomorrow,
  isWithinInterval,
  startOfWeek,
} from "date-fns";
import { CalendarIcon } from "lucide-react";

import { Card } from "@/components/card";
import { SessionCard } from "@/features/session/components/session-card";
import {
  PublicSession,
  PublicSessionWithHostOrganisation,
} from "@classview/shared-types";

import { AcceptSessionModal } from "./accept-session-modal";
import { RejectSessionModal } from "./reject-session-modal";

type PendingSessionListProps = {
  sessions: PublicSessionWithHostOrganisation[];
};

type GroupedSessions = {
  today: PublicSessionWithHostOrganisation[];
  tomorrow: PublicSessionWithHostOrganisation[];
  nextWeek: PublicSessionWithHostOrganisation[];
  later: PublicSessionWithHostOrganisation[];
};

function groupSessions(
  sessions: PublicSessionWithHostOrganisation[],
): GroupedSessions {
  const nextWeekStart = startOfWeek(addWeeks(new Date(), 1));
  const nextWeekEnd = addWeeks(nextWeekStart, 1);

  // First group the sessions
  const grouped = sessions.reduce(
    (groups, session) => {
      const startDate = new Date(session.start);

      if (isToday(startDate)) {
        groups.today.push(session);
      } else if (isTomorrow(startDate)) {
        groups.tomorrow.push(session);
      } else if (
        isWithinInterval(startDate, { start: nextWeekStart, end: nextWeekEnd })
      ) {
        groups.nextWeek.push(session);
      } else {
        groups.later.push(session);
      }

      return groups;
    },
    {
      today: [],
      tomorrow: [],
      nextWeek: [],
      later: [],
    } as GroupedSessions,
  );

  // Then sort each group by start time
  const sortByStartTime = (a: PublicSession, b: PublicSession) =>
    new Date(a.start).getTime() - new Date(b.start).getTime();

  return {
    today: grouped.today.sort(sortByStartTime),
    tomorrow: grouped.tomorrow.sort(sortByStartTime),
    nextWeek: grouped.nextWeek.sort(sortByStartTime),
    later: grouped.later.sort(sortByStartTime),
  };
}

function SessionGroup({
  title,
  sessions,
}: {
  title: string;
  sessions: PublicSessionWithHostOrganisation[];
}) {
  if (sessions.length === 0) return null;

  return (
    <div className="relative space-y-4">
      <div>
        <div className="absolute -left-6 top-1 inline-block rounded-full border border-secondary bg-white p-0.5 ring-8 ring-white">
          <div className="size-2 rounded-full bg-secondary"></div>
        </div>
        <p className="text-sm font-semibold text-secondary">{title}</p>
        <p className="text-sm text-muted">
          {sessions.length} session{sessions.length === 1 ? "" : "s"}
        </p>
      </div>
      {sessions.map((session) => (
        <Card key={session.id} as="article">
          <Card.Content className="space-y-4">
            <SessionCard
              session={{
                ...session,
                name: `${session.hostOrganisation.name}: ${session.name}`,
              }}
            />

            <div className="flex items-center justify-end gap-2">
              <RejectSessionModal session={session} />
              <AcceptSessionModal session={session} />
            </div>
          </Card.Content>
        </Card>
      ))}
    </div>
  );
}

export function PendingSessionList(props: PendingSessionListProps) {
  const { sessions } = props;

  if (sessions.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <p className="mb-2 flex flex-col items-center justify-center">
          <CalendarIcon className="size-6 text-secondary" />
        </p>
        <p className="font-semibold text-foreground">No pending sessions!</p>
      </div>
    );
  }

  const groupedSessions = groupSessions(sessions);

  return (
    <div className="space-y-8 border-l-2 border-border pl-4">
      <SessionGroup title="Today" sessions={groupedSessions.today} />
      <SessionGroup title="Tomorrow" sessions={groupedSessions.tomorrow} />
      <SessionGroup title="Next Week" sessions={groupedSessions.nextWeek} />
      <SessionGroup title="Later" sessions={groupedSessions.later} />
    </div>
  );
}
